
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface BlogPost {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  date: string;
  author?: string;
  featured?: boolean;
}

export interface BlogPostCardProps {
  post: BlogPost;
}

export function BlogPostCard({ post }: BlogPostCardProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Format date to localized string based on current language
  const formattedDate = new Date(post.date).toLocaleDateString(
    i18n.language === 'ja' ? 'ja-JP' : 'en-US', 
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
  );

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow duration-200 flex flex-col h-full">
      <div className="relative h-48 overflow-hidden bg-muted">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
      </div>
      <CardHeader>
        <div className="flex justify-between items-start mb-2">
          <Badge variant="outline" className="bg-primary/10 text-primary hover:bg-primary/20">
            {post.category}
          </Badge>
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="h-3 w-3 mr-1" />
            {formattedDate}
          </div>
        </div>
        <h3 className="font-semibold text-xl line-clamp-2 hover:text-primary transition-colors">
          {post.title}
        </h3>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-muted-foreground line-clamp-3 text-sm">
          {post.description}
        </p>
      </CardContent>
      <CardFooter className="flex justify-between items-center pt-4 border-t">
        <span className="text-sm font-medium">{post.author || ''}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(`/blog/${post.id}`)}
          className="text-primary hover:text-primary-foreground hover:bg-primary/90"
        >
          {t('blog.readMore')}
        </Button>
      </CardFooter>
    </Card>
  );
}
